import React, { MouseEventHandler, ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import clsx from 'clsx'

import { makeStyles } from '@material-ui/core/styles'
import { Box, Collapse, FormHelperText, Grid } from '@material-ui/core'

import { DefaultRadioItem } from './DefaultRadioItem'
import { DefaultRadioOption } from './DefaultRadioGroup'
import { AccountStoresFormProps } from './AccountStoresForm'

import { StoreAccountModel } from '../types/accountProps'

import { EditStoreModel } from '../server/mpsklad_core/Models/EditStoreModel'
import { StoreModelBase } from '../server/mpsklad_core/Models/StoreModelBase'
import { WarehouseModel } from '../server/mpsklad_core/Models/WarehouseModel'
import { StoreRelationType } from '../server/mpsklad_core/Entity/Base/StoreRelationType'
import { EditSimpleStoresModel } from '../server/mpsklad_core/Models/EditSimpleStoresModel'

export type RelationTypeRadioProps<TWarehouse extends WarehouseModel> =
  Pick<AccountStoresFormProps<StoreAccountModel, StoreModelBase, EditStoreModel, TWarehouse>, 'hint'>
  & {
  option: DefaultRadioOption<StoreRelationType>

  formData: EditSimpleStoresModel

  isSubmitting: boolean

  onRelationTypeClick: (newValue: StoreRelationType) => MouseEventHandler<unknown>

  renderStoreForm: () => ReactNode
}

export const RelationTypeRadio =
  observer(
    <TWarehouse extends WarehouseModel>
    ({
       option, hint,
       formData,
       isSubmitting,
       onRelationTypeClick,
       renderStoreForm
     }: RelationTypeRadioProps<TWarehouse>) => {
      const classes = useStyles()

      return (
        <Grid item xs={12}>
          <Grid container direction="column" className={clsx(classes.settingsTopic, 'default-border')}>
            <Grid item xs={12}>
              <DefaultRadioItem
                disabled={isSubmitting}
                value={formData.relationType}
                option={option}
                onClick={onRelationTypeClick(option.value)}
                optionRadioClassName={classes.relationRadio}
                optionClassName={classes.relationOption}
                optionLabelClassName={clsx(classes.subHeader, classes.relationLabel)}
              />
            </Grid>

            <Grid item xs={12} className={classes.relationContent}>
              <Collapse in={formData.relationType === option.value}>
                {
                  !!hint &&
                  <Box marginTop={3}>
                    <FormHelperText>
                      {hint}
                    </FormHelperText>
                  </Box>
                }

                {
                  formData.relationType === option.value &&
                  renderStoreForm()
                }
              </Collapse>
            </Grid>
          </Grid>
        </Grid>
      )
    })

const useStyles = makeStyles(
  ({spacing, palette}) => ({
    settingsTopic: {
      borderRadius: 12,
      padding: spacing(3)
    },
    subHeader: {
      fontSize: '14px',
      fontWeight: 700,
      lineHeight: '16.8px',
      textTransform: 'uppercase'
    },
    relationRadio: {
      marginTop: -22
    },
    relationOption: {
      '& span': {
        color: '#1F364D',
        lineHeight: '14.4px',
        textAlign: 'left'
      },
      '& .Mui-disabled span': {
        color: `${palette.mpSkladPrimary.gray} !important`
      },
      '& span:not(.Mui-disabled)': {
        color: '#3987CF'
      }
    },
    relationLabel: {
      fontWeight: 700,
      color: '#1F364D !important'
    },
    relationContent: {
      paddingLeft: 42
    }
  }))